import React from 'react';
import {
  Monitor,
  Maximize2,
  MousePointer,
  Keyboard,
  Gamepad2,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { useLivePreviewStore } from '../../store/livePreviewStore';
import { getPresetById } from '../../utils/device-presets';

export const ViewportStatusBar: React.FC = () => {
  const {
    viewport,
    playState,
    stats,
    inputState,
    inputSimulationEnabled,
    currentFrame,
  } = useLivePreviewStore();

  const preset = viewport.devicePresetId ? getPresetById(viewport.devicePresetId) : null;

  return (
    <div className="flex items-center gap-4 px-3 py-1.5 bg-zinc-800 border-t border-zinc-700 text-xs text-zinc-400">
      {/* Device info */}
      <div className="flex items-center gap-1.5">
        <Monitor size={12} />
        <span>
          {preset ? preset.name : 'Custom'} ({viewport.customWidth} x {viewport.customHeight})
        </span>
      </div>

      {/* Scale */}
      <div className="flex items-center gap-1.5">
        <Maximize2 size={12} />
        <span>{Math.round(viewport.scale * 100)}%</span>
      </div>

      {/* Play state */}
      <div className={cn(
        'px-2 py-0.5 rounded text-xs font-medium',
        playState === 'playing' && 'bg-green-600/20 text-green-400',
        playState === 'paused' && 'bg-yellow-600/20 text-yellow-400',
        playState === 'stopped' && 'bg-zinc-700 text-zinc-400'
      )}>
        {playState === 'playing' ? 'Playing' : playState === 'paused' ? 'Paused' : 'Stopped'}
      </div>

      {/* Frame counter (when paused) */}
      {playState === 'paused' && (
        <div className="text-yellow-400">
          Frame: {currentFrame}
        </div>
      )}

      {/* FPS */}
      <div className={cn(
        stats.fps < 30 && 'text-red-400',
        stats.fps >= 30 && stats.fps < 55 && 'text-yellow-400',
        stats.fps >= 55 && 'text-green-400'
      )}>
        {stats.fps} FPS
      </div>

      <div className="flex-1" />

      {/* Input indicators */}
      {inputSimulationEnabled && (
        <div className="flex items-center gap-2">
          {/* Mouse position */}
          <div className="flex items-center gap-1">
            <MousePointer size={12} />
            <span>
              {inputState.mousePosition.x}, {inputState.mousePosition.y}
            </span>
          </div>

          {/* Active keys */}
          {inputState.keys.size > 0 && (
            <div className="flex items-center gap-1">
              <Keyboard size={12} />
              <span>{Array.from(inputState.keys).join('+')}</span>
            </div>
          )}

          {/* Gamepad */}
          {inputState.gamepadConnected && (
            <div className="flex items-center gap-1 text-green-400">
              <Gamepad2 size={12} />
              <span>Connected</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
